/**
 * PatternGrid — tracker view of one pattern.
 *
 * Phase 1 step 6: rendering only.
 * Phase 1 step 7: cursor visualization + edit mode hint.
 *
 *   row | Pulse 1   Pulse 2   Tri    Noise        DPCM
 *   00  | C-5 D1 0C  E-5 D0 .. A-2   lng P05 06   KICK11
 *   01  | --- -- --  --- -- -- ---   --- -- --    --- --
 *   ...
 *
 * The currently playing row gets a cyan tint; the cursor cell gets an
 * amber outline. They can overlap when the cursor sits on the playing row.
 */

import type { DemoRow } from '../audio/demo-song';
import {
  CHANNEL_SPECS,
  formatDPCMCell,
  formatNoiseCell,
  formatPulseCell,
  formatTriangleCell,
} from '../lib/cell-format';
import { CHANNEL_ORDER, type ChannelName } from '../lib/keyboard';

interface Props {
  rows: DemoRow[];
  patternIndex: number;
  /** Row currently playing, if any (rendered with playhead highlight). */
  currentRow: number | null;
  /** Cursor row in this pattern (rendered with outline). null when grid unfocused. */
  cursorRow: number | null;
  /** Cursor channel index into CHANNEL_ORDER. */
  cursorChannel: number | null;
  /** Edit mode badge — when on, typing notes auto-advances row. */
  editMode: boolean;
}

const COLUMN_KEYS: ChannelName[] = [...CHANNEL_ORDER];

function HeaderCell({
  label,
  colorClass,
  widthCh,
  active,
}: {
  label: string;
  colorClass: string;
  widthCh: number;
  active: boolean;
}) {
  return (
    <span
      className={`mr-3 ${colorClass} ${active ? 'text-nes-white font-bold' : ''}`}
      style={{ width: `${widthCh}ch` }}
    >
      {label}
    </span>
  );
}

export function PatternGrid({
  rows,
  patternIndex,
  currentRow,
  cursorRow,
  cursorChannel,
  editMode,
}: Props) {
  return (
    <div className="overflow-x-auto rounded border border-nes-cyan/30 bg-nes-black">
      <div className="font-mono text-[11px] leading-tight">
        <header className="flex items-center justify-between gap-2 border-b border-nes-cyan/20 bg-nes-dim px-2 py-1">
          <span className="text-[10px] uppercase tracking-wider text-nes-white/40">
            Pattern {patternIndex.toString().padStart(2, '0')}
          </span>
          {editMode && (
            <span className="rounded bg-nes-red/30 px-2 py-[1px] text-[10px] font-bold text-nes-red">
              ● REC
            </span>
          )}
        </header>

        <div className="flex border-b border-nes-cyan/10 bg-nes-dim/50 px-2 py-1 text-[10px] uppercase tracking-wider text-nes-white/40">
          <span className="w-8 text-nes-white/50">row</span>
          {COLUMN_KEYS.map((key, idx) => {
            const spec = CHANNEL_SPECS[key];
            return (
              <HeaderCell
                key={key}
                label={spec.label}
                colorClass={spec.colorClass}
                widthCh={spec.widthCh}
                active={cursorChannel === idx}
              />
            );
          })}
        </div>

        <div>
          {rows.map((row, idx) => {
            const isPlaying = idx === currentRow;
            const isCursor = idx === cursorRow;
            const isBeat = idx % 4 === 0;
            const rowBg = isPlaying
              ? 'bg-nes-cyan/20'
              : isCursor
                ? 'bg-nes-white/[0.06]'
                : isBeat
                  ? 'bg-nes-white/[0.04]'
                  : '';
            const indicator = isPlaying ? '▶' : isCursor ? '·' : ' ';
            const rowText = isPlaying ? 'text-nes-cyan' : 'text-nes-white/80';

            return (
              <div key={idx} className={`flex px-2 py-[1px] ${rowBg} ${rowText}`}>
                <span className="w-8 text-nes-white/40">
                  {indicator}
                  {idx.toString().padStart(2, '0').padEnd(3, ' ')}
                </span>
                <CellSpan
                  active={isCursor && cursorChannel === 0}
                  text={formatPulseCell(row.pulse1)}
                  colorClass={CHANNEL_SPECS.pulse1.colorClass}
                  widthCh={CHANNEL_SPECS.pulse1.widthCh}
                />
                <CellSpan
                  active={isCursor && cursorChannel === 1}
                  text={formatPulseCell(row.pulse2)}
                  colorClass={CHANNEL_SPECS.pulse2.colorClass}
                  widthCh={CHANNEL_SPECS.pulse2.widthCh}
                />
                <CellSpan
                  active={isCursor && cursorChannel === 2}
                  text={formatTriangleCell(row.triangle)}
                  colorClass={CHANNEL_SPECS.triangle.colorClass}
                  widthCh={CHANNEL_SPECS.triangle.widthCh}
                />
                <CellSpan
                  active={isCursor && cursorChannel === 3}
                  text={formatNoiseCell(row.noise)}
                  colorClass={CHANNEL_SPECS.noise.colorClass}
                  widthCh={CHANNEL_SPECS.noise.widthCh}
                />
                <CellSpan
                  active={isCursor && cursorChannel === 4}
                  text={formatDPCMCell(row.dpcm)}
                  colorClass={CHANNEL_SPECS.dpcm.colorClass}
                  widthCh={CHANNEL_SPECS.dpcm.widthCh}
                  last
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CellSpan({
  active,
  text,
  colorClass,
  widthCh,
  last,
}: {
  active: boolean;
  text: string;
  colorClass: string;
  widthCh: number;
  last?: boolean;
}) {
  return (
    <span
      className={`${last ? '' : 'mr-3'} whitespace-pre ${colorClass} ${
        active ? 'rounded-sm bg-amber-300/20 outline outline-1 outline-amber-300/70' : ''
      }`}
      style={{ width: `${widthCh}ch` }}
    >
      {text}
    </span>
  );
}
